"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LayoutEntitiesController = void 0;
const decorators_1 = require("../../../api-exposer/decorators");
const layout_traversal_1 = require("../utils/layout-traversal");
/**
 * @internal
 * Controller for the layout entities apis, allowing alterations to be applied
 * to an instance of layout manager in a structured manner.
 */
class LayoutEntitiesController {
    constructor(
    // workaround to prevent importing entire GoldenLayouts, also re-written at build time to type any
    layoutManager, layoutContentCache) {
        this.layoutManager = layoutManager;
        this.layoutContentCache = layoutContentCache;
        this.analytics = (topic) => {
            this.wire.sendAction(`layout-controller-${topic}`).catch(() => {
                // don't expose
            });
        };
        /**
         * @internal
         * @returns the root contentItem of the layout.
         */
        this.getRoot = () => {
            this.analytics('get-root');
            const root = this.layoutManager.layout.root.contentItems[0];
            return {
                // TODO: fix typing
                type: root.type,
                entityId: this.layoutContentCache.getOrCreateEntityId(root)
            };
        };
        /**
         * @internal
         * Retrieves the containing stack of a given view identity.
         * @param view Identity of the view to retrieve the stack of.
         * @returns Stack containing the given view.
         * @throws If the view does not belong to a stack within the layout manager.
         */
        this.getStackByView = async (view) => {
            var _a, _b, _c, _d;
            this.analytics('get-stack-by-view');
            const viewComponent = this.layoutManager.getViewComponent(view);
            if (!viewComponent || ((_b = (_a = viewComponent.container) === null || _a === void 0 ? void 0 : _a.parent) === null || _b === void 0 ? void 0 : _b.parent.type) !== 'stack') {
                return undefined;
            }
            const stack = (_d = (_c = viewComponent.container) === null || _c === void 0 ? void 0 : _c.parent) === null || _d === void 0 ? void 0 : _d.parent;
            return { entityId: this.layoutContentCache.getOrCreateEntityId(stack), type: 'stack' };
        };
        /**
         * @internal
         * Returns all views belonging to a given stack
         *
         * NOTE: Due to (TODO: ticket) Golden Layouts has an issue which changes the order of tabs
         * when the amount of new views to add overflows the width of the container. This results
         * in tabs being re-ordered in the UI, while the underlying content item array remains in the
         * original order specified. So calling getStackViews() will return this underlying array order,
         * with indexes that will not match up to the render-order of the tabs.
         *
         * @param id - Entity id of the stack.
         * @returns A list of view identities in order of appearance.
         * @throws if the content item associated with the entity id does not exist or is not a stack.
         */
        this.getStackViews = (id) => {
            this.analytics('get-stack-views');
            const stack = this.layoutContentCache.getContentItemOrThrow(id, ['stack']);
            const views = stack.contentItems.map((ci) => {
                var _a;
                return ({
                    name: (_a = ci.config.componentState) === null || _a === void 0 ? void 0 : _a.name,
                    uuid: this.layoutManager.ofWindow.identity.uuid
                });
            });
            return views;
        };
        /**
         * @internal
         * @param id Entity id associated with a layout content item.
         * @returns true if the given entity is the root content item, false otherwise.
         */
        this.isRoot = (id) => {
            var _a;
            this.analytics('is-root');
            const ci = this.layoutContentCache.getContentItemOrThrow(id);
            return !!((_a = ci.parent) === null || _a === void 0 ? void 0 : _a.isRoot);
        };
        /**
         * @internal
         * Checks whether the given entity exists.
         * @param entityId Id of a content item within the layout
         * @returns True if the content item exists and belongs to the layout.
         */
        this.exists = (entityId) => {
            this.analytics('exists');
            return this.layoutContentCache.hasKey(entityId);
        };
        /**
         * @internal
         * Adds an existing view to the stack, or creates and adds a view to the given stack.
         *
         * NOTE: Due to (TODO: ticket) Golden Layouts has an issue which changes the order of tabs
         * when the amount of new views to add overflows the width of the container. This results
         * in tabs being re-ordered in the UI, while the underlying content item array remains in the
         * original order specified. So calling getStackViews() will return this underlying array order,
         * with indexes that will not match up to the render-order of the tabs.
         *
         * @param stackEntityId Entity id of the stack content item within the layout.
         * @param viewCreationOrReference View identity or creation options
         * @param options Optional view options: index number used to insert the view into the stack at that index. Defaults to 0 (front of the stack)
         * @returns Promise resolving with the identity of the newly added view.
         * @throws If the view does not exist, fails to create, or the stack does not exist.
         */
        this.addViewToStack = async (stackEntityId, viewCreationOrReference, { index } = { index: 0 }) => {
            this.analytics('add-view-to-stack');
            const currentStack = this.layoutContentCache.getContentItemOrThrow(stackEntityId);
            if (index && index > currentStack.contentItems.length + 1) {
                throw new Error(`Index '${index}' out of range, please exclude the index or specify a number between 0 and ${currentStack.contentItems.length}`);
            }
            const location = {
                id: stackEntityId,
                index
            };
            const { identity } = await this.layoutManager.platform.createView(viewCreationOrReference, {
                ...this.layoutManager.ofWindow.identity,
                location,
            });
            return identity;
        };
        this.findViewInStack = (stack, viewIdentity) => {
            return stack.contentItems.find((ci) => { var _a, _b; return ((_b = (_a = ci.config) === null || _a === void 0 ? void 0 : _a.componentState) === null || _b === void 0 ? void 0 : _b.name) === viewIdentity.name; });
        };
        /**
         * @internal
         * Removes a view from the given stack. If it's the only view, the stack will be destroyed, unless window creation
         * option closeOnLastViewRemoved is set to false.
         *
         * @param stackEntityId Entity id of a stack content item to remove the view from.
         * @param view Identity of the view to remove.
         * @throws If the stack does not exist or the view does not exist or belong to the stack.
         */
        this.removeViewFromStack = async (stackEntityId, view) => {
            this.analytics('remove-view-from-stack');
            const stack = this.layoutContentCache.getContentItemOrThrow(stackEntityId, ['stack']);
            const viewInstance = this.findViewInStack(stack, view);
            if (!viewInstance) {
                throw new Error(`Tried to remove a view ('${view.name}') which does not belong to the stack.`);
            }
            await this.layoutManager.platform.closeView(view);
        };
        /**
         * @internal
         * Creates a new adjacent 'stack' and adds the views to it at the specified position
         * @param targetId Entity id of the content item to add a stack adjacent to it
         * @param views List of identities or view creation options of the views to include in the stack
         * @param options Creation options, defaults to { position: 'right' }
         * @returns the Entity Id of the new stack
         */
        this.createAdjacentStack = async (targetId, views, { position = 'right' } = {}) => {
            this.analytics('create-adjacent-stack');
            if (!Array.isArray(views) || views.length === 0) {
                throw new Error('The parameter "views" must be an array with at least 1 element.');
            }
            if (!['top', 'bottom', 'left', 'right'].includes(position)) {
                throw new Error(`Invalid position '${position}' specified.`);
            }
            const currentStack = this.layoutContentCache.getContentItemOrThrow(targetId);
            const { parent } = currentStack;
            const containerType = ['top', 'bottom'].includes(position) ? 'column' : 'row';
            const newContainer = this.layoutManager.layout.createContentItem({
                type: containerType,
            });
            const newStack = this.layoutManager.layout.createContentItem({
                type: 'stack'
            });
            parent.replaceChild(currentStack, newContainer);
            newContainer.addChild(currentStack);
            const offset = ['left', 'top'].includes(position) ? -1 : 1;
            const newStackIndex = newContainer.contentItems.indexOf(currentStack) + offset;
            newContainer.addChild(newStack, newStackIndex);
            const entityId = this.layoutContentCache.getOrCreateEntityId(newStack);
            // !! reverse the views list so we can utilize index=0 (prepending each tab)
            // which will maintain correct order in the underlying GL structure and avoids the reflow bug
            await Promise.all(views.reverse().map((view) => this.addViewToStack(entityId, view)));
            return entityId;
        };
        this.getAdjacentStacks = async ({ targetId, edge }) => {
            this.analytics('get-adjacent-stacks');
            const contentItem = this.layoutContentCache.getContentItemOrThrow(targetId);
            // call utils helper to traverse and return adjacent stacks
            return (0, layout_traversal_1.getAdjacentStacks)(contentItem, edge).map((stack) => ({
                entityId: this.layoutContentCache.getOrCreateEntityId(stack),
            }));
        };
        this.setStackActiveView = async (stackEntityId, viewIdentity) => {
            this.analytics('set-stack-active-view');
            const stack = this.layoutContentCache.getContentItemOrThrow(stackEntityId, ['stack']);
            const view = this.findViewInStack(stack, viewIdentity);
            if (!view) {
                throw new Error(`Tried to set a view ('${viewIdentity.name}') as active when it does not belong to the stack.`);
            }
            stack.setActiveContentItem(view, true);
        };
        this.wire = this.layoutManager.platform.wire;
    }
    /**
     * @internal
     * Retrieves the content of a column or row and adds each to the
     * entity cache to allow them to be addressed externally.
     * @param id Entity id of the Column Or Row to retrieve the content of.
     * @returns Array of layout entity definitions
     * @throws if the entity associated with {@link id} is not in the entity cache, does not belogn to a layout, or is not a column/row.
     */
    getContent(id) {
        this.analytics('get-content');
        const ci = this.layoutContentCache.getContentItemOrThrow(id, ['column', 'row']);
        return ci.contentItems.map((item) => ({
            type: item.type,
            entityId: this.layoutContentCache.getOrCreateEntityId(item)
        }));
    }
    ;
    /**
     * @internal
     * Retrieves the parent content item of the given entity, and adds it to the entity cache
     * so it can be addressed externally.
     * @param id Entity id associated with a layout content item.
     * @returns An entity definition for the given entity's parent, or undefined if the entity is the top level
     * content item or has been removed from the layout entirely.
     */
    getParent(id) {
        this.analytics('get-parent');
        const ci = this.layoutContentCache.getContentItemOrThrow(id);
        if (ci.parent && !ci.parent.isRoot && ci.parent.contentItems.includes(ci)) {
            return {
                type: ci.parent.type,
                entityId: this.layoutContentCache.getOrCreateEntityId(ci.parent)
            };
        }
        return undefined;
    }
    ;
}
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "getRoot", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "getStackByView", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "getStackViews", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "getContent", null);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "getParent", null);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "isRoot", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "exists", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "addViewToStack", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "removeViewFromStack", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "createAdjacentStack", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "getAdjacentStacks", void 0);
__decorate([
    (0, decorators_1.expose)()
], LayoutEntitiesController.prototype, "setStackActiveView", void 0);
exports.LayoutEntitiesController = LayoutEntitiesController;
